import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import { BUTTON_CONST } from '@/src/constants/buttons';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { NAV_CONST } from '@/src/constants/nav';
import dayjs from 'dayjs';
import {
  getBannerByIdAPI,
  getBannerPageContentCatetoryAPI,
  putBannerByIdAPI,
} from '@/src/apis/rest/banner';
import { PutBannerByIdRequestBody } from '@/src/apis/rest/banner.dtos';
import { z } from 'zod';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DateRange } from 'react-day-picker';
import { usePrompt } from '@/src/hooks/usePrompt';
import { Form, FormField } from '@/src/components/ui/form';
import { useToast } from '@/src/hooks/useToast';
import { TOAST_CONST } from '@/src/constants/toast';
import SectionCard from '@/src/components/molecule/SectionCard';
import FormContent from '@/src/components/molecule/FormContent';
import DateRangePicker from '@/src/components/atom/DateRangePicker';
import RadioGroup from '@/src/components/atom/RadioGroup';
import Input from '@/src/components/atom/Input';
import ImageUpload from '@/src/components/atom/ImageUpload';
import IconButton from '@/src/components/atom/Button/IconButton';
import { Media } from '@/src/models/MeidaDto';
import ConnectContentsModal from './components/BannerConnectedTypesModal';
import SectionLabel from '@/src/components/molecule/SectionLabel';
import Checkbox from '@/src/components/atom/Checkbox';
import Label from '@/src/components/atom/Label';
import { urlRegex } from '@/src/utils/regex';
import Select from '@/src/components/atom/Select';
import { BannerContentCategoryType } from '@/src/models/BannerDto';
import ImageUploadInfo from '@/components/molecule/ImageUploadInfo';
import {
  CONNECT_CONTENT_OPTIONS,
  OPTIONS_CONST,
} from '@/src/constants/options';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';
import Typo from '@/src/components/atom/Typo';

type BannerPlaceType = 'home' | 'donate' | 'community' | 'challenge';

type ModalState = 'NONE' | 'CONNECT_CONTENTS' | 'DELETE';

const BACK_PATH = PATH.SUB_BANNER_DETAIL;

const bannerFormSchema = () => {
  return z.object({
    bannerType: z.string(),
    bannerTitle: z.string().min(1),
    bannerShowedYn: z.boolean(),
    bannerMarketingYn: z.boolean(),
    bannerPostStartedAt: z.string(),
    bannerPostEndedAt: z.string(),
    mediaId: z.number(),
    bannerHomeShowedYn: z.boolean(),
    bannerDonationShowedYn: z.boolean(),
    bannerCommunityShowedYn: z.boolean(),
    bannerChallengeShowedYn: z.boolean(),
    bannerPlaceType: z.array(z.string()).min(1),
    bannerContentType: z.string(),
    bannerAppPageConnectionCategory: z.string().optional().nullable(),
    bannerContentCategory: z.string().optional().nullable(),
    bannerContentId: z.number().optional().nullable(),
    bannerExternalUrl: z.string().optional().nullable(),
  });
};

const DEFAULT_VALUES: Partial<
  PutBannerByIdRequestBody & {
    bannerPostedAt: DateRange;
    bannerPlaceType: BannerPlaceType[];
  }
> = {
  bannerType: '상세 배너',
  bannerTitle: '',
  bannerShowedYn: true,
  bannerMarketingYn: true,
  bannerPostedAt: { from: undefined, to: undefined },
  bannerHomeShowedYn: false,
  bannerDonationShowedYn: false,
  bannerCommunityShowedYn: false,
  bannerChallengeShowedYn: false,
  bannerPlaceType: [],
};

export default function SubBannerEditPage() {
  // util
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();

  // query
  const { data: bannerContentCategoryOptions } = useQuery({
    queryKey: ['banner-page-content-category-options'],
    queryFn: getBannerPageContentCatetoryAPI,
    select: data =>
      (data?.rows || []).map(item => ({ label: item.value, value: item.key })),
  });
  const { data: bannerData } = useQuery({
    queryKey: ['banner', id],
    queryFn: () => getBannerByIdAPI({ id: Number(id) }),
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH, { replace: true });
      },
    },
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
  });

  // form
  const form = useForm<
    PutBannerByIdRequestBody & {
      bannerPostedAt: DateRange;
      bannerImage?: Media;
      bannerPlaceType: BannerPlaceType[];
    }
  >({
    resolver: zodResolver(bannerFormSchema()),
    defaultValues: DEFAULT_VALUES,
  });
  const { field: mediaField } = useController({
    name: 'bannerImage',
    control: form.control,
  });

  const getIsContentValid = () => {
    switch (form.watch().bannerContentType) {
      case 'bannerAppPageConnection':
        return !!form.watch().bannerAppPageConnectionCategory;
      case 'bannerContentConnection':
        return !!form.watch().bannerContentCategory;
      case 'bannerExternalUrlConnection':
        return (
          !!form.watch().bannerExternalUrl &&
          urlRegex.test(form.watch().bannerExternalUrl || '')
        );
      default:
        return false;
    }
  };

  // state
  const [modalState, setModalState] = useState<ModalState>('NONE');
  const [defaultData, setDefaultData] = useState('');
  const [selectedTitle, setSelectedTitle] = useState('');
  const [, setSelectedCategory] = useState('');
  const [isPorceReplace] = useState(false);
  const {
    Dialog: EditDialog,
    isOpen: isEditOpen,
    openDialog: openEditDialog,
    closeDialog: closeEditDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);
  const [dataPage, setDataPage] = useState(1);
  const [dataPageSize, setDataPageSize] = useState(10);
  const [isExUrlError, setIsExUrlError] = useState(false);

  // mutation
  const putBannerMutation = useMutation({ mutationFn: putBannerByIdAPI });

  // event
  const handleOpenModal = (type: ModalState) => {
    setModalState(type);
  };
  const handleCloseModal = () => {
    setModalState('NONE');
  };
  const handleToggleModal = (type: ModalState) => {
    if (modalState === type) {
      handleCloseModal();
    } else {
      setModalState(type);
    }
  };
  const handleGoBack = () => {
    navigate(-1);
  };

  const handleBannerPlaceTypeChange = (
    type: BannerPlaceType,
    value: boolean,
  ) => {
    switch (type) {
      case 'home':
        form.setValue('bannerHomeShowedYn', value);
        break;
      case 'donate':
        form.setValue('bannerDonationShowedYn', value);
        break;
      case 'community':
        form.setValue('bannerCommunityShowedYn', value);
        break;
      case 'challenge':
        form.setValue('bannerChallengeShowedYn', value);
        break;
      default:
        break;
    }
  };

  const handleSubmit = (values: any) => {
    const newValues = {
      ...values,
      // bannerContentCategory: undefined,
      // bannerAppPageConnectionCategory: undefined,
      // bannerContentId: undefined,
      // bannerExternalUrl: undefined,
      // bannerContentType: undefined,
    };

    delete newValues.media;
    delete newValues.contentId;

    putBannerMutation.mutate(
      { ...newValues, id: Number(id) },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });

          navigate(BACK_PATH.replace(':id', String(id)));
        },
        onError: e => {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        },
      },
    );
  };

  const handleResetContent = () => {
    form.setValue('bannerAppPageConnectionCategory', null);
    form.setValue('bannerContentCategory', null);
    form.setValue('bannerContentId', null);
    form.setValue('bannerExternalUrl', null);

    setSelectedTitle('');
    setDataPage(1);
    setDataPageSize(10);
  };

  const getContentTitleWithCategory = useCallback(
    (contentTitle: string, contentCategory: BannerContentCategoryType) => {
      const categoryKr = contentCategory
        ? CONNECT_CONTENT_OPTIONS[contentCategory]
        : '-';
      return `[${categoryKr}] ${contentTitle || '-'}`;
    },
    [],
  );

  const handleOkClick = useCallback(
    (e: {
      id: number;
      title: string;
      category: BannerContentCategoryType;
      dataPage: number;
      dataPageSize: number;
    }) => {
      const titleWithCategory = getContentTitleWithCategory(
        e.title,
        e.category,
      );
      setSelectedTitle(titleWithCategory);
      setSelectedCategory(e.category);

      form.setValue('bannerContentId', e.id);
      form.setValue('bannerContentCategory', e.category);

      setDataPage(e.dataPage);
      setDataPageSize(e.dataPageSize);
    },
    [form, getContentTitleWithCategory],
  );

  // render
  const renderConnectContent = () => {
    switch (form.watch('bannerContentType')) {
      case 'bannerAppPageConnection':
        return (
          <FormField
            control={form.control}
            name="bannerAppPageConnectionCategory"
            rules={{
              required: true,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <FormContent isRequired label="연결 컨텐츠" error={error}>
                  <Select
                    rootProps={{
                      value: field?.value || undefined,
                      onValueChange: field.onChange,
                    }}
                    valueProps={{
                      placeholder: '연결할 앱 내 화면을 선택해주세요',
                    }}
                    option={[
                      {
                        optionList: bannerContentCategoryOptions || [],
                      },
                    ]}
                  />
                </FormContent>
              );
            }}
          />
        );
      case 'bannerContentConnection':
        return (
          <FormField
            control={form.control}
            name="bannerContentId"
            render={({ fieldState: { error } }) => {
              return (
                <FormContent
                  isRequired
                  label="연결 컨텐츠"
                  error={error}
                  defaultMessage={
                    <span className="mt-[4px] flex gap-[5px]">
                      <span className="flex h-[18px] items-center">
                        <LucideIcon name="Info" size={16} color="gray5" />
                      </span>
                      <Typo typoType="b14r" typoColor="gray5">
                        컨텐츠 상세 화면은 게시 시작 일시 기준으로 노출되는
                        컨텐츠만 연결할 수 있어요
                      </Typo>
                    </span>
                  }
                >
                  <div className="flex gap-2">
                    <Input
                      value={selectedTitle}
                      disabled
                      containerClassName="flex-1"
                      placeholder="상세 연결 될 컨텐츠를 선택해주세요"
                    />
                    <Button
                      buttonSize="medium"
                      buttonColor="secondary"
                      className="h-[40px]"
                      onClick={() => handleOpenModal('CONNECT_CONTENTS')}
                      disabled={
                        !(
                          form.watch('bannerPostedAt')?.from &&
                          form.watch('bannerPostedAt')?.to
                        )
                      }
                    >
                      연결 컨텐츠 선택
                    </Button>
                  </div>
                </FormContent>
              );
            }}
          />
        );
      case 'bannerExternalUrlConnection':
        return (
          <FormField
            control={form.control}
            name="bannerExternalUrl"
            render={({ field }) => {
              return (
                <FormContent
                  isRequired
                  label="연결 컨텐츠"
                  error={
                    isExUrlError
                      ? {
                          type: 'pattern',
                          message:
                            'url 형식을 확인해주세요 (https://example.com)',
                        }
                      : undefined
                  }
                >
                  <Input
                    value={field?.value || undefined}
                    onChange={e => {
                      field.onChange(e.target.value);

                      if (e.target.value) {
                        setIsExUrlError(!urlRegex.test(e.target.value));
                      } else {
                        setIsExUrlError(false);
                      }
                    }}
                    onClear={() => {
                      field.onChange('');
                      setIsExUrlError(false);
                    }}
                    containerClassName="flex-1"
                    placeholder="링크를 입력해주세요 (https://example.com)"
                  />
                </FormContent>
              );
            }}
          />
        );
      default:
        return (
          <FormField
            control={form.control}
            name="bannerContentId"
            render={({ fieldState: { error } }) => {
              return (
                <FormContent isRequired label="연결 컨텐츠" error={error}>
                  <div className="flex gap-2">
                    <Input
                      value={selectedTitle}
                      disabled
                      containerClassName="flex-1"
                      placeholder="상세 연결 될 컨텐츠를 선택해주세요"
                    />
                    <Button
                      buttonSize="medium"
                      buttonColor="secondary"
                      className="h-[40px]"
                      onClick={() => handleOpenModal('CONNECT_CONTENTS')}
                      disabled
                    >
                      연결 컨텐츠 선택
                    </Button>
                  </div>
                </FormContent>
              );
            }}
          />
        );
    }
  };

  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <div className="flex items-center gap-2">
            {/* <Button
              className="w-40"
              buttonSize="medium"
              buttonColor="red"
              onClick={() => handleOpenModal('DELETE')}
              isLoading={deleteBannerMutation.isPending}
            >
              {BUTTON_CONST.delete}
            </Button> */}
            <Button
              type="button"
              onClick={openEditDialog}
              disabled={
                !(defaultData !== JSON.stringify(form.watch())) ||
                !form.formState.isValid ||
                !getIsContentValid()
              }
              className="w-40"
              buttonSize="medium"
              isLoading={putBannerMutation.isPending}
            >
              {BUTTON_CONST.save}
            </Button>
          </div>
        </div>
      </BottomFixedContainer>
    );
  };
  const getDateDisabledMatcher = (date: Date) => {
    if (dayjs().isAfter(date, 'D')) return true;

    return false;
  };

  // effect
  useEffect(() => {
    if (bannerData) {
      const bannerPlaceTypeResult = [];

      if (bannerData?.bannerHomeShowedYn) {
        bannerPlaceTypeResult.push('home');
      }
      if (bannerData?.bannerDonationShowedYn) {
        bannerPlaceTypeResult.push('donate');
      }
      if (bannerData?.bannerCommunityShowedYn) {
        bannerPlaceTypeResult.push('community');
      }
      if (bannerData?.bannerChallengeShowedYn) {
        bannerPlaceTypeResult.push('challenge');
      }

      const defaultFormData = {
        ...bannerData,
        bannerPlaceType: bannerPlaceTypeResult as BannerPlaceType[],
        bannerPostedAt: {
          from: new Date(bannerData.bannerPostStartedAt),
          to: new Date(bannerData.bannerPostEndedAt),
        },
        mediaId: bannerData?.bannerImage?.id,
      };

      form.reset(defaultFormData);

      const titleWithCategory = getContentTitleWithCategory(
        bannerData.contentTitle || '',
        bannerData.bannerContentCategory,
      );

      setSelectedTitle(titleWithCategory);

      setDefaultData(JSON.stringify(defaultFormData));
    }
  }, [bannerData, form, getContentTitleWithCategory]);

  // usePrompt
  usePrompt(
    defaultData !== JSON.stringify(form.watch()) &&
      !form.formState.isSubmitted &&
      !isPorceReplace,
  );

  return (
    <>
      <MainContentTitle
        breadcrumbList={[NAV_CONST.subBanner, NAV_CONST.subBannerEdit]}
        pageTitle={NAV_CONST.subBannerEdit}
      />
      <Form {...form}>
        <form
          ref={formRef}
          className="relative mb-[100px]"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <SectionCard cardTitle="서브 배너 정보">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-x-6 gap-y-4">
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="bannerPostedAt"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent isRequired label="게시 기간" error={error}>
                        <DateRangePicker
                          {...field}
                          onChange={e => {
                            field.onChange(e);

                            form.setValue(
                              'bannerPostStartedAt',
                              e?.from
                                ? dayjs(e?.from).format('YYYY-MM-DD')
                                : '',
                            );
                            form.setValue(
                              'bannerPostEndedAt',
                              e?.to ? dayjs(e?.to).format('YYYY-MM-DD') : '',
                            );
                          }}
                          dateDisabledMatcher={getDateDisabledMatcher}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="bannerShowedYn"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="노출 여부" isRequired error={error}>
                        <RadioGroup
                          optionList={[
                            { label: OPTIONS_CONST.showed, value: 'true' },
                            { label: OPTIONS_CONST.notShowed, value: 'false' },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: (e: string) =>
                              field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="bannerTitle"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="제목" isRequired error={error}>
                        <Input
                          {...field}
                          containerClassName="flex-1"
                          placeholder="제목을 입력해주세요"
                          onClear={() => {
                            field.onChange('');
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="mediaId"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="배너 이미지"
                        isRequired
                        error={error}
                        defaultMessage={
                          <ImageUploadInfo widthSize={335} heightSize={96} />
                        }
                      >
                        <ImageUpload
                          width={335}
                          height={96}
                          widthText={335}
                          heightText={96}
                          maxImageSizeMb={5}
                          imageClassName="object-cover"
                          value={mediaField.value?.bufferFullUrl}
                          onChange={e => {
                            field.onChange(e.id);
                            mediaField.onChange(e);
                          }}
                          topAccessary={({ handleEdit, handleRemove }) => {
                            return (
                              <>
                                <div className="flex gap-x-1">
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'Pencil' }}
                                    onClick={() => {
                                      handleEdit();
                                    }}
                                  />
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'X' }}
                                    onClick={() => {
                                      handleRemove();
                                      field.onChange(undefined);
                                    }}
                                  />
                                </div>
                              </>
                            );
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
              </div>
              <div className="border-r border-r-gray1" />
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="bannerContentType"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="컨텐츠 유형 선택"
                        isRequired
                        error={error}
                      >
                        <RadioGroup
                          optionList={[
                            {
                              label: '앱 내 화면',
                              value: 'bannerAppPageConnection',
                            },
                            {
                              label: '컨텐츠 상세 연결',
                              value: 'bannerContentConnection',
                            },
                            {
                              label: '외부 링크 연결',
                              value: 'bannerExternalUrlConnection',
                            },
                          ]}
                          rootProps={{
                            value: field.value,
                            onValueChange: e => {
                              field.onChange(e);
                              setIsExUrlError(false);
                              handleResetContent();
                            },
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                {form.watch()?.bannerContentType ? (
                  renderConnectContent()
                ) : (
                  <div className="h-[40px]" />
                )}
                <FormField
                  control={form.control}
                  name="bannerPlaceType"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="노출 영역" isRequired error={error}>
                        <div className="flex h-full min-h-[40px] w-full flex-1 items-center gap-x-4">
                          {(
                            [
                              { value: 'home', label: '홈' },
                              { value: 'donate', label: '기부하기' },
                              { value: 'community', label: '커뮤니티' },
                              { value: 'challenge', label: '챌린지' },
                            ] as const
                          )?.map((item, idx) => {
                            return (
                              <div
                                className="flex items-center gap-x-2"
                                key={idx.toString()}
                              >
                                <Checkbox
                                  id={`${item.value}-${item.label}`}
                                  checked={field.value?.includes(item.value)}
                                  onCheckedChange={checked => {
                                    handleBannerPlaceTypeChange(
                                      item.value,
                                      !!checked,
                                    );

                                    return checked
                                      ? field.onChange([
                                          ...field.value,
                                          item.value,
                                        ])
                                      : field.onChange(
                                          field.value?.filter(
                                            (value: string) =>
                                              value !== item.value,
                                          ),
                                        );
                                  }}
                                />
                                <Label
                                  htmlFor={`${item.value}-${item.label}`}
                                  className="text-b16r text-gray6"
                                >
                                  {item.label}
                                </Label>
                              </div>
                            );
                          })}
                        </div>
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="bannerMarketingYn"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="광고 여부" isRequired error={error}>
                        <RadioGroup
                          optionList={[
                            {
                              label: OPTIONS_CONST.advertisement,
                              value: 'true',
                            },
                            {
                              label: OPTIONS_CONST.notAdvertisement,
                              value: 'false',
                            },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: (e: string) =>
                              field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <SectionLabel
                  labelProps={{
                    className: 'translate-y-2',
                  }}
                  label="등록 일시"
                  value={
                    <Input
                      containerClassName="flex-1"
                      value={dayjs(bannerData?.createdAt).format(
                        'YYYY-MM-DD HH:mm:ss',
                      )}
                      disabled
                    />
                  }
                />
                <SectionLabel
                  labelProps={{
                    className: 'translate-y-2',
                  }}
                  label="등록 관리자"
                  value={
                    <Input
                      containerClassName="flex-1"
                      value={bannerData?.administrator?.name}
                      disabled
                    />
                  }
                />
              </div>
            </div>
          </SectionCard>
          {renderBottomFixedContent()}
        </form>
      </Form>

      {/* 모달 */}
      {modalState === 'CONNECT_CONTENTS' && (
        <ConnectContentsModal
          selectedCategory={form.watch().bannerContentCategory || undefined}
          selectedId={form.watch().bannerContentId || undefined}
          open={modalState === 'CONNECT_CONTENTS'}
          onOpenChange={() => handleToggleModal('CONNECT_CONTENTS')}
          bannerPostedAt={form.watch('bannerPostedAt')}
          onOkClick={handleOkClick}
          dataPage={dataPage}
          dataPageSize={dataPageSize}
        />
      )}
      {isEditOpen && (
        <EditDialog
          open={isEditOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openEditDialog();
            } else {
              closeEditDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.edit}
          isLoading={putBannerMutation.isPending}
        />
      )}
    </>
  );
}
